import { cn } from '@/lib/utils';
import { Fragment } from 'react';
import { CircleMarker, MapContainer, TileLayer, Tooltip } from 'react-leaflet';
import {
  MAP_ATTRIBUTION,
  MAP_LABELS_URL,
  MAP_MARKER_COLOR,
  MAP_MARKER_COLOR_MUTED,
  MAP_TILE_URL,
} from '../constants';

export interface PlaceMapPoint {
  id?: string | number;
  latitude: number;
  longitude: number;
  name?: string;
  active?: boolean;
}

interface PlaceMapProps {
  points: PlaceMapPoint[];
  className?: string;
  zoom?: number;
  showLabels?: boolean;
  onPointClick?: (point: PlaceMapPoint) => void;
}

export const PlaceMap = ({
  points,
  className,
  zoom = 13,
  showLabels,
  onPointClick,
}: PlaceMapProps) => {
  if (!points.length) return null;

  const single = points.length === 1 ? points[0] : undefined;
  const bounds = points.map(
    (point) => [point.latitude, point.longitude] as [number, number],
  );

  return (
    <MapContainer
      /* Nuqtalar o'zgarsa xarita qayta markazlashishi uchun. */
      key={bounds.map((b) => b.join()).join('|')}
      /* Konteyner o'lchami leaflet ishga tushgandan keyin aniqlansa, tayllar
         so'ralmay qoladi — shuning uchun o'lchamni majburan qayta hisoblaymiz. */
      ref={(map) => {
        if (map) setTimeout(() => map.invalidateSize(), 0);
      }}
      center={single ? [single.latitude, single.longitude] : undefined}
      zoom={single ? zoom : undefined}
      bounds={single ? undefined : bounds}
      boundsOptions={{ padding: [24, 24] }}
      scrollWheelZoom={false}
      /* z-0 — leaflet o'z panellariga yuqori z-index beradi, sahifa ustiga chiqmasin. */
      className={cn('z-0 h-full w-full', className)}
    >
      {/* Plitka manzillari constants/map.ts da. */}
      <TileLayer
        url={MAP_TILE_URL}
        attribution={MAP_ATTRIBUTION}
        maxNativeZoom={16}
      />
      {MAP_LABELS_URL && <TileLayer url={MAP_LABELS_URL} maxNativeZoom={16} />}

      {points.map((point, index) => {
        const color = point.active ? MAP_MARKER_COLOR : MAP_MARKER_COLOR_MUTED;
        const eventHandlers = onPointClick
          ? { click: () => onPointClick(point) }
          : undefined;

        return (
          <Fragment key={`${point.latitude}-${point.longitude}-${index}`}>
            {/* Tashqi halqa — aktiv nuqtada kattaroq va neon soyali. */}
            <CircleMarker
              center={[point.latitude, point.longitude]}
              radius={point.active ? 10 : 7}
              eventHandlers={eventHandlers}
              pathOptions={{
                color,
                fillColor: color,
                fillOpacity: point.active ? 0.2 : 0.12,
                weight: 2,
                opacity: point.active ? 1 : 0.7,
                className: cn(
                  onPointClick && 'cursor-pointer',
                  point.active && 'map-marker-active',
                ),
              }}
            >
              {point.name && (
                <Tooltip
                  permanent={showLabels}
                  direction="top"
                  offset={[0, -10]}
                >
                  {point.name}
                </Tooltip>
              )}
            </CircleMarker>

            {/* Ichki to'la nuqta. */}
            <CircleMarker
              center={[point.latitude, point.longitude]}
              radius={point.active ? 4 : 3}
              eventHandlers={eventHandlers}
              pathOptions={{
                color,
                fillColor: color,
                fillOpacity: 1,
                weight: 0,
                opacity: point.active ? 1 : 0.7,
                className: cn(
                  onPointClick && 'cursor-pointer',
                  point.active && 'map-marker-active',
                ),
              }}
            />
          </Fragment>
        );
      })}
    </MapContainer>
  );
};
